import { AfterViewInit, Component, Inject, inject } from '@angular/core';
import { DOCUMENT } from '@angular/common';

import { ErrorScreenLayoutComponent } from '../shared/error-screen-layout.component';
import {
  ErrorScreenCopy,
  ErrorScreenCopyByLocale,
} from '../shared/error-screen-copy';
import { resolveErrorScreenLocale } from '../shared/error-screen-locale';
import { ErrorScreenTelemetryService } from 'src/app/core/services/error-screen-telemetry.service';
import { ErrorScreenRecoveryService } from 'src/app/core/services/error-screen-recovery.service';
import {
  ERROR_SCREEN_ARRIVAL_REASON_STATE_KEY,
  ERROR_SCREEN_CAUSE_STATE_KEY,
  ErrorScreenRecoveryAction,
  normalizeArrivalReason,
} from 'src/app/core/domain/error-screen';
import { createUnicornIncidentCode } from 'src/app/core/domain/unicorn';

/**
 * Spec 051 (error-screen-parity).
 * Phase 3: FR-008 translated (en/es + English fallback), FR-009 no status-code
 * heading, FR-010 independent of the failing application.
 * Phase 2: FR-005/FR-007 — access is denied to the current session, so the
 * state-changing action is to re-authenticate: a fresh token can pick up
 * newly-granted roles (and, where the handoff cause is `auth`, this IS the
 * FR-007 re-authenticate for an expired/invalid session). "Go to my start" is a
 * secondary escape (`/` is not a 403 source). FR-012 records whether it worked.
 */
export const FORBIDDEN_COPY = {
  en: {
    logoAlt: 'rettX',
    heading: 'You don’t have access to this',
    body:
      'This area isn’t available to your account. If you think you should be able to see it, ask whoever manages your access. In the meantime you can return to the home screen.',
    signInLink: 'Sign in again',
    homeLink: 'Go to the home screen',
  },
  es: {
    logoAlt: 'rettX',
    heading: 'No tienes acceso a esto',
    body:
      'Esta sección no está disponible para tu cuenta. Si crees que deberías poder verla, pídeselo a quien gestiona tus accesos. Mientras tanto, puedes volver a la pantalla de inicio.',
    signInLink: 'Iniciar sesión de nuevo',
    homeLink: 'Ir a la pantalla de inicio',
  },
} satisfies ErrorScreenCopyByLocale;

@Component({
  selector: 'app-page-error-forbidden-403',
  standalone: true,
  imports: [ErrorScreenLayoutComponent],
  templateUrl: './page-error-forbidden.component.html',
  styleUrl: './page-error-forbidden.component.scss',
})
export class ForbiddenComponent implements AfterViewInit {
  private telemetry = inject(ErrorScreenTelemetryService);
  private recovery = inject(ErrorScreenRecoveryService);
  private recorded = false;
  readonly copy: ErrorScreenCopy;
  readonly incidentCode = createUnicornIncidentCode();
  private readonly arrivalReason: string;
  private readonly cause: unknown;

  constructor(@Inject(DOCUMENT) document: Document) {
    this.copy = FORBIDDEN_COPY[resolveErrorScreenLocale(document)];
    const handoff = this.telemetry.readHandoffState();
    this.arrivalReason = normalizeArrivalReason(
      handoff?.[ERROR_SCREEN_ARRIVAL_REASON_STATE_KEY],
    );
    this.cause = handoff?.[ERROR_SCREEN_CAUSE_STATE_KEY];
  }

  // Spec 051 FR-001/FR-002/FR-004: record this screen's appearance after render,
  // carrying any machine-readable reason handed over on arrival.
  ngAfterViewInit(): void {
    if (this.recorded) {
      return;
    }
    this.recorded = true;
    this.telemetry.recordScreenShown('forbidden', {
      cause: this.cause,
      arrivalReason: this.arrivalReason,
    });
  }

  // Spec 051 Phase 2 FR-005/FR-007: re-authenticate to change the failing state
  // (fresh claims, or a new session where the old one expired); "go to my start"
  // is the secondary escape. Never a dead end.
  recover(action: ErrorScreenRecoveryAction): void {
    this.recovery.recover({
      screen: 'forbidden',
      action,
      incidentCode: this.incidentCode,
      arrivalReason: this.arrivalReason,
      destination: action === 'interactive-reauth' ? '/patients' : '/',
    });
  }
}
